// Incidents CP2 — detail page. Four regions: header (linked-entity chips as
// navigable links), details + amend (AmendIncidentModal → UPDATE, audit trigger
// records it automatically), status control (explicit open→under_review→closed,
// reopen allowed — each a single-column UPDATE), and a merged timeline from
// THREE sources (narrative incident_updates + audit_events amendments +
// incident_photos), newest-first.
//
// PHOTO LIGHTBOX DECISION: JobPhotoStrip's exported Lightbox hardcodes
// getJobPhotoSignedUrl (the job-photos bucket) inside its own useSignedThumbUrl
// hook — the helper isn't injectable, so reusing it would fetch incident photos
// from the wrong bucket. Reuse was NOT cheap; thumbnails here open the
// short-lived signed URL in a new tab instead (the dispatch's fallback).
//
// AUDIT PAYLOAD SHAPE (verified against dev, not assumed): trg_log_incident_event
// writes ONE audit_events row per UPDATE statement (a single multi-field UPDATE
// → one row), action='updated', with before/after carrying the FULL incident row
// as jsonb. Field-level diffs are derived by comparing before[k] vs after[k];
// updated_at (always bumped by the trigger) and immutable columns are excluded.

import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatRelativeDate } from '@/lib/utils'
import {
  ArrowLeft, X, AlertTriangle, Truck, Wrench, ClipboardList, User as UserIcon,
  Image as ImageIcon, Pencil, MessageSquarePlus, History, Loader2, Send,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  uploadIncidentPhoto, getIncidentPhotoSignedUrl, PHOTO_ACCEPT_ATTR,
} from '@/lib/incidentPhotos'

interface Incident {
  id: string; workspace_id: string; occurred_at: string; title: string; description: string
  severity: string; status: string; location_text: string | null
  vehicle_id: string | null; field_job_id: string | null; request_id: string | null
  involved_agent_id: string | null; reported_by: string; created_at: string; updated_at: string
}
interface UpdateRow { id: string; body: string; created_by: string; created_at: string }
interface AuditRow { id: string; action: string; before: Record<string, unknown> | null; after: Record<string, unknown> | null; actor_id: string | null; actor_email: string | null; created_at: string }
interface PhotoRow { id: string; storage_path: string; caption: string | null; uploaded_by: string | null; uploaded_at: string }

const SEVERITY: Record<string, { label: string; color: string; bg: string }> = {
  low:    { label: 'Low',    color: 'var(--ink-tertiary)', bg: 'var(--surface-sunken)' },
  normal: { label: 'Normal', color: 'var(--info)',         bg: 'var(--info-dim)' },
  high:   { label: 'High',   color: 'var(--warning)',      bg: 'var(--warning-dim)' },
  urgent: { label: 'Urgent', color: 'var(--danger)',       bg: 'var(--danger-dim)' },
}
const STATUS: Record<string, { label: string; color: string; bg: string }> = {
  open:         { label: 'Open',         color: 'var(--info)',         bg: 'var(--info-dim)' },
  under_review: { label: 'Under review', color: 'var(--warning)',      bg: 'var(--warning-dim)' },
  closed:       { label: 'Closed',       color: 'var(--ink-tertiary)', bg: 'var(--surface-sunken)' },
}
const STATUS_ORDER = ['open', 'under_review', 'closed'] as const

const field: React.CSSProperties = { padding: '8px 10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box', width: '100%' }
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 5 }
const card: React.CSSProperties = { background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '16px 18px' }

// Fields whose changes render as timeline amendment diffs. Immutable columns and
// updated_at (bumped on every UPDATE) are deliberately absent.
const DIFF_FIELDS: Record<string, string> = {
  status: 'Status', severity: 'Severity', title: 'Title', description: 'Description',
  location_text: 'Location', occurred_at: 'Occurred at',
  vehicle_id: 'Vehicle', field_job_id: 'Job', request_id: 'Request', involved_agent_id: 'Involved tech',
}

export default function IncidentDetail() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { workspace } = useAuth()
  const qc = useQueryClient()
  const [amendOpen, setAmendOpen] = useState(false)
  const wsId = workspace?.id ?? null

  const { data: incident, isLoading, error } = useQuery<Incident | null>({
    queryKey: ['incident', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('incidents').select('*').eq('id', id).maybeSingle()
      if (error) { console.error('[incident] load failed', error); throw error }
      return (data as Incident) ?? null
    },
    enabled: !!id && !!wsId,
  })

  const { data: updates = [] } = useQuery<UpdateRow[]>({
    queryKey: ['incident-updates', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('incident_updates').select('id, body, created_by, created_at').eq('incident_id', id).order('created_at', { ascending: false })
      if (error) { console.error('[incident-updates] load failed', error); throw error }
      return (data ?? []) as UpdateRow[]
    },
    enabled: !!id,
  })

  const { data: audits = [] } = useQuery<AuditRow[]>({
    queryKey: ['incident-audits', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('audit_events').select('id, action, before, after, actor_id, actor_email, created_at').eq('entity_type', 'incidents').eq('entity_id', id).order('created_at', { ascending: false })
      if (error) { console.error('[incident-audits] load failed', error); throw error }
      return (data ?? []) as AuditRow[]
    },
    enabled: !!id,
  })

  const { data: photos = [] } = useQuery<PhotoRow[]>({
    queryKey: ['incident-photos', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('incident_photos').select('id, storage_path, caption, uploaded_by, uploaded_at').eq('incident_id', id).order('uploaded_at', { ascending: false })
      if (error) { console.error('[incident-photos] load failed', error); throw error }
      return (data ?? []) as PhotoRow[]
    },
    enabled: !!id,
  })

  // Cheap label maps (names for chips + amendment diffs + amend-modal pickers).
  const { data: profiles = [] } = useQuery<Array<{ id: string; full_name: string | null }>>({
    queryKey: ['incidents-profiles', wsId],
    queryFn: async () => (await supabase.from('profiles').select('id, full_name').eq('workspace_id', wsId!)).data ?? [],
    enabled: !!wsId,
  })
  const { data: vehicles = [] } = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ['incidents-vehicles', wsId],
    queryFn: async () => (await supabase.from('vehicles').select('id, name').eq('workspace_id', wsId!).order('name')).data ?? [],
    enabled: !!wsId,
  })
  const { data: jobs = [] } = useQuery<Array<{ id: string; job_number: string | null; title: string | null }>>({
    queryKey: ['incidents-jobs', wsId],
    queryFn: async () => (await supabase.from('field_jobs').select('id, job_number, title').eq('workspace_id', wsId!).order('created_at', { ascending: false }).limit(100)).data ?? [],
    enabled: !!wsId,
  })
  const { data: requests = [] } = useQuery<Array<{ id: string; subject: string; status: string }>>({
    queryKey: ['incidents-requests-all', wsId],
    queryFn: async () => (await supabase.from('portal_requests').select('id, subject, status').eq('workspace_id', wsId!).order('created_at', { ascending: false }).limit(200)).data ?? [],
    enabled: !!wsId,
  })

  const pName = useMemo(() => new Map(profiles.map(p => [p.id, p.full_name ?? 'Unknown'])), [profiles])
  const vName = useMemo(() => new Map(vehicles.map(v => [v.id, v.name])), [vehicles])
  const jName = useMemo(() => new Map(jobs.map(j => [j.id, j.job_number || j.title || 'Job'])), [jobs])
  const rName = useMemo(() => new Map(requests.map(r => [r.id, r.subject])), [requests])

  // Resolve a field's stored value to a human string for amendment diffs.
  const resolveVal = (key: string, raw: unknown): string => {
    if (raw == null || raw === '') return '—'
    const s = String(raw)
    if (key === 'status') return STATUS[s]?.label ?? s
    if (key === 'severity') return SEVERITY[s]?.label ?? s
    if (key === 'vehicle_id') return vName.get(s) ?? 'Vehicle'
    if (key === 'field_job_id') return jName.get(s) ?? 'Job'
    if (key === 'request_id') return rName.get(s) ?? 'Request'
    if (key === 'involved_agent_id') return pName.get(s) ?? 'Tech'
    if (key === 'occurred_at') return formatRelativeDate(s)
    return s
  }

  // Short-diff fields render "from → to" inline; longer text fields render as
  // "amended" to keep the timeline scannable.
  const SHORT = new Set(['status', 'severity', 'vehicle_id', 'field_job_id', 'request_id', 'involved_agent_id'])

  type TL =
    | { kind: 'update'; ts: string; key: string; body: string; by: string }
    | { kind: 'amend'; ts: string; key: string; diffs: Array<{ label: string; from: string; to: string; short: boolean }>; by: string }
    | { kind: 'photo'; ts: string; key: string; photo: PhotoRow }

  const timeline: TL[] = useMemo(() => {
    const items: TL[] = []
    for (const u of updates) items.push({ kind: 'update', ts: u.created_at, key: `u-${u.id}`, body: u.body, by: pName.get(u.created_by) ?? 'Unknown' })
    for (const a of audits) {
      const before = a.before ?? {}; const after = a.after ?? {}
      const diffs: Array<{ label: string; from: string; to: string; short: boolean }> = []
      for (const [key, label] of Object.entries(DIFF_FIELDS)) {
        if (String((before as any)[key] ?? '') !== String((after as any)[key] ?? '')) {
          diffs.push({ label, from: resolveVal(key, (before as any)[key]), to: resolveVal(key, (after as any)[key]), short: SHORT.has(key) })
        }
      }
      items.push({ kind: 'amend', ts: a.created_at, key: `a-${a.id}`, diffs, by: a.actor_id ? (pName.get(a.actor_id) ?? a.actor_email ?? 'Someone') : (a.actor_email ?? 'Someone') })
    }
    for (const ph of photos) items.push({ kind: 'photo', ts: ph.uploaded_at, key: `p-${ph.id}`, photo: ph })
    return items.sort((x, y) => (x.ts < y.ts ? 1 : x.ts > y.ts ? -1 : 0))
  }, [updates, audits, photos, pName, vName, jName, rName])

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['incident', id] })
    qc.invalidateQueries({ queryKey: ['incident-audits', id] })
    qc.invalidateQueries({ queryKey: ['incident-updates', id] })
    qc.invalidateQueries({ queryKey: ['incident-photos', id] })
    qc.invalidateQueries({ queryKey: ['incidents', wsId] })
  }

  const setStatus = useMutation({
    mutationFn: async (next: string) => {
      const { error } = await supabase.from('incidents').update({ status: next }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => { toast.success('Status updated'); invalidateAll() },
    onError: (e: any) => toast.error(e?.message || 'Failed to update status'),
  })

  if (error) return <Shell><div style={{ ...card, color: 'var(--danger)', background: 'var(--danger-dim)', borderColor: 'var(--danger-border)' }}>Couldn’t load this incident. Please refresh.</div></Shell>
  if (isLoading) return <Shell><p style={{ color: 'var(--ink-tertiary)', fontSize: 13 }}>Loading…</p></Shell>
  if (!incident) return <Shell><div style={card}><p style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink-primary)', margin: 0 }}>Incident not found</p><button onClick={() => navigate('/app/incidents')} style={linkBtn}>← Back to incidents</button></div></Shell>

  const sev = SEVERITY[incident.severity] ?? SEVERITY.normal
  const st = STATUS[incident.status] ?? STATUS.open

  return (
    <Shell>
      <button onClick={() => navigate('/app/incidents')} style={{ ...linkBtn, marginBottom: 14, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <ArrowLeft style={{ width: 15, height: 15 }} /> Incidents
      </button>

      {/* a) HEADER */}
      <div style={{ ...card, borderLeft: `3px solid ${sev.color}`, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <span style={pill(sev.bg, sev.color)}>{sev.label}</span>
          <span style={pill(st.bg, st.color)}>{st.label}</span>
          <span style={{ fontSize: 12, color: 'var(--ink-tertiary)', marginLeft: 'auto' }}>Occurred {formatRelativeDate(incident.occurred_at)}</span>
        </div>
        <h1 style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 10px', color: 'var(--ink-primary)' }}>{incident.title}</h1>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {chip(<UserIcon style={ic} />, `Reported by ${pName.get(incident.reported_by) ?? 'Unknown'}`)}
          {incident.location_text && chip(<AlertTriangle style={ic} />, incident.location_text)}
          {incident.involved_agent_id && chip(<UserIcon style={ic} />, pName.get(incident.involved_agent_id) ?? 'Tech')}
          {incident.vehicle_id && <Link to={`/app/fleet/vehicles/${incident.vehicle_id}`} style={chipLink}><Truck style={ic} />{vName.get(incident.vehicle_id) ?? 'Vehicle'}</Link>}
          {incident.field_job_id && <Link to={`/app/jobs/${incident.field_job_id}`} style={chipLink}><Wrench style={ic} />{jName.get(incident.field_job_id) ?? 'Job'}</Link>}
          {incident.request_id && <Link to={`/app/requests`} style={chipLink}><ClipboardList style={ic} />{rName.get(incident.request_id) ?? 'Request'}</Link>}
        </div>
      </div>

      {/* b) DETAILS + AMEND */}
      <div style={{ ...card, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <p style={sectionLbl}>Details</p>
          <button type="button" onClick={() => setAmendOpen(true)} style={secondaryBtn}><Pencil style={{ width: 13, height: 13 }} /> Amend</button>
        </div>
        <p style={{ fontSize: 14, color: 'var(--ink-secondary)', margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{incident.description}</p>
      </div>

      {/* c) STATUS CONTROL */}
      <div style={{ ...card, marginBottom: 14 }}>
        <p style={sectionLbl}>Status</p>
        <div style={{ display: 'inline-flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          {STATUS_ORDER.map(s => {
            const active = incident.status === s
            const m = STATUS[s]
            return (
              <button key={s} type="button" disabled={active || setStatus.isPending} onClick={() => setStatus.mutate(s)}
                style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid', borderColor: active ? m.color : 'var(--border-default)', background: active ? m.bg : 'transparent', color: active ? m.color : 'var(--ink-secondary)', fontSize: 13, fontWeight: 600, cursor: active ? 'default' : 'pointer', opacity: setStatus.isPending && !active ? 0.6 : 1 }}>
                {m.label}
              </button>
            )
          })}
        </div>
        {incident.status === 'closed' && <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '8px 0 0' }}>Closed incidents can be reopened — select Under review.</p>}
      </div>

      {/* e) PHOTOS */}
      <div style={{ ...card, marginBottom: 14 }}>
        <PhotoSection incidentId={id} workspaceId={wsId} photos={photos} onChanged={() => qc.invalidateQueries({ queryKey: ['incident-photos', id] })} />
      </div>

      {/* d) TIMELINE (composer + merged sources) */}
      <div style={card}>
        <p style={sectionLbl}><History style={{ width: 13, height: 13, verticalAlign: '-2px', marginRight: 5 }} />Timeline</p>
        <UpdateComposer incidentId={id} workspaceId={wsId} onAdded={() => qc.invalidateQueries({ queryKey: ['incident-updates', id] })} />
        {timeline.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '12px 0 0' }}>No updates, amendments, or photos yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginTop: 12 }}>
            {timeline.map(item => <TimelineEntry key={item.key} item={item} />)}
          </div>
        )}
      </div>

      <AmendIncidentModal open={amendOpen} onClose={() => setAmendOpen(false)} incident={incident}
        vehicles={vehicles} jobs={jobs} requests={requests} profiles={profiles}
        onSaved={() => { setAmendOpen(false); invalidateAll() }} />
    </Shell>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div style={{ maxWidth: 820, margin: '0 auto', padding: '24px 20px' }}>{children}</div>
}

// ─── Timeline entry (one visual language, type-distinct icons) ────────────────
function TimelineEntry({ item }: { item: any }) {
  const icon = item.kind === 'update' ? <MessageSquarePlus style={ic2} /> : item.kind === 'amend' ? <Pencil style={ic2} /> : <ImageIcon style={ic2} />
  const iconBg = item.kind === 'update' ? 'var(--info-dim)' : item.kind === 'amend' ? 'var(--warning-dim)' : 'var(--accent-dim)'
  const iconFg = item.kind === 'update' ? 'var(--info)' : item.kind === 'amend' ? 'var(--warning)' : 'var(--accent-text)'
  return (
    <div style={{ display: 'flex', gap: 10, padding: '10px 0', borderTop: '1px solid var(--border-subtle)' }}>
      <div style={{ width: 26, height: 26, borderRadius: 7, background: iconBg, color: iconFg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {item.kind === 'update' && <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{item.body}</p>}
        {item.kind === 'amend' && (
          item.diffs.length === 0 ? <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: 0 }}>Details amended</p> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {item.diffs.map((d: any, i: number) => (
                <p key={i} style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: 0 }}>
                  <span style={{ fontWeight: 600, color: 'var(--ink-primary)' }}>{d.label}:</span>{' '}
                  {d.short ? <>{d.from} <span style={{ color: 'var(--ink-tertiary)' }}>→</span> {d.to}</> : 'amended'}
                </p>
              ))}
            </div>
          )
        )}
        {item.kind === 'photo' && <PhotoTimelineThumb photo={item.photo} />}
        <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>{item.by ? `${item.by} · ` : ''}{formatRelativeDate(item.ts)}</p>
      </div>
    </div>
  )
}

function PhotoTimelineThumb({ photo }: { photo: PhotoRow }) {
  const { data: url } = useQuery({
    queryKey: ['incident-photo-url', photo.storage_path],
    queryFn: async () => { const { url } = await getIncidentPhotoSignedUrl(photo.storage_path, 300); return url },
    staleTime: 290_000, gcTime: 290_000, retry: 1,
  })
  return (
    <div>
      <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: '0 0 5px' }}>Photo added{photo.caption ? ` — ${photo.caption}` : ''}</p>
      {url ? (
        <button type="button" onClick={() => window.open(url, '_blank', 'noopener,noreferrer')} title="Open full size"
          style={{ padding: 0, border: '1px solid var(--border-subtle)', borderRadius: 8, background: 'var(--hover-bg)', cursor: 'pointer', width: 72, height: 72, overflow: 'hidden' }}>
          <img src={url} alt={photo.caption || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" />
        </button>
      ) : <div style={{ width: 72, height: 72, borderRadius: 8, background: 'var(--hover-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Loader2 style={{ width: 14, height: 14, color: 'var(--ink-tertiary)', animation: 'spin 1s linear infinite' }} /></div>}
    </div>
  )
}

// ─── Update composer (narrative append) ───────────────────────────────────────
function UpdateComposer({ incidentId, workspaceId, onAdded }: { incidentId: string; workspaceId: string | null; onAdded: () => void }) {
  const { profile } = useAuth()
  const [body, setBody] = useState('')
  const add = useMutation({
    mutationFn: async () => {
      if (!workspaceId || !profile?.id) throw new Error('Not signed in')
      if (!body.trim()) throw new Error('Update is empty')
      const { error } = await supabase.from('incident_updates').insert({ workspace_id: workspaceId, incident_id: incidentId, body: body.trim(), created_by: profile.id })
      if (error) throw error
    },
    onSuccess: () => { setBody(''); onAdded() },
    onError: (e: any) => toast.error(e?.message || 'Failed to add update'),
  })
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
      <textarea value={body} onChange={e => setBody(e.target.value)} rows={2} placeholder="Add an update — a call back, a status note, next steps…"
        style={{ ...field, resize: 'vertical', flex: 1 }} />
      <button type="button" onClick={() => add.mutate()} disabled={!body.trim() || add.isPending}
        style={{ padding: '8px 14px', background: 'var(--accent)', border: 'none', borderRadius: 8, cursor: body.trim() && !add.isPending ? 'pointer' : 'not-allowed', color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, opacity: body.trim() && !add.isPending ? 1 : 0.5, display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
        <Send style={{ width: 13, height: 13 }} /> {add.isPending ? 'Adding…' : 'Add'}
      </button>
    </div>
  )
}

// ─── Photo section (append-only, thumbnails open signed URL in a new tab) ─────
function PhotoSection({ incidentId, workspaceId, photos, onChanged }: { incidentId: string; workspaceId: string | null; photos: PhotoRow[]; onChanged: () => void }) {
  const [inFlight, setInFlight] = useState(0)
  const onFiles = async (files: FileList) => {
    if (!workspaceId) { toast.error('Not signed in'); return }
    const arr = Array.from(files)
    setInFlight(n => n + arr.length)
    let failed = 0
    for (const f of arr) {
      const { error } = await uploadIncidentPhoto({ workspaceId, incidentId, file: f })
      if (error) { console.error('[incident] photo upload failed', error); failed++ }
    }
    setInFlight(n => Math.max(0, n - arr.length))
    if (failed > 0) toast.error(`${failed} photo${failed > 1 ? 's' : ''} failed to upload`)
    onChanged()
  }
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: photos.length || inFlight ? 10 : 0 }}>
        <p style={sectionLbl}>Photos</p>
        <label style={{ ...secondaryBtn, cursor: 'pointer' }}>
          <ImageIcon style={{ width: 13, height: 13 }} /> Add photos
          <input type="file" multiple accept={PHOTO_ACCEPT_ATTR} onChange={e => { if (e.target.files?.length) { onFiles(e.target.files); e.target.value = '' } }} style={{ display: 'none' }} />
        </label>
      </div>
      {photos.length === 0 && inFlight === 0 ? (
        <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>Evidence photos are append-only — once added, they can’t be removed.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 8 }}>
          {photos.map(p => <PhotoThumb key={p.id} photo={p} />)}
          {Array.from({ length: inFlight }).map((_, i) => (
            <div key={`pending-${i}`} style={{ aspectRatio: '1', borderRadius: 8, background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Loader2 style={{ width: 16, height: 16, color: 'var(--ink-tertiary)', animation: 'spin 1s linear infinite' }} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function PhotoThumb({ photo }: { photo: PhotoRow }) {
  const { data: url, isLoading } = useQuery({
    queryKey: ['incident-photo-url', photo.storage_path],
    queryFn: async () => { const { url } = await getIncidentPhotoSignedUrl(photo.storage_path, 300); return url },
    staleTime: 290_000, gcTime: 290_000, retry: 1,
  })
  return (
    <button type="button" onClick={() => url && window.open(url, '_blank', 'noopener,noreferrer')} title="Open full size" aria-label="Open photo"
      style={{ aspectRatio: '1', padding: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border-subtle)', background: 'var(--hover-bg)', cursor: url ? 'pointer' : 'default' }}>
      {url && !isLoading ? (
        <img src={url} alt={photo.caption || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" />
      ) : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Loader2 style={{ width: 14, height: 14, color: 'var(--ink-tertiary)', animation: 'spin 1s linear infinite' }} /></div>}
    </button>
  )
}

// ─── Amend modal (all incident fields except status; save = UPDATE) ───────────
function AmendIncidentModal({ open, onClose, onSaved, incident, vehicles, jobs, requests, profiles }: {
  open: boolean; onClose: () => void; onSaved: () => void; incident: Incident
  vehicles: Array<{ id: string; name: string }>
  jobs: Array<{ id: string; job_number: string | null; title: string | null }>
  requests: Array<{ id: string; subject: string; status: string }>
  profiles: Array<{ id: string; full_name: string | null }>
}) {
  const [occurredAt, setOccurredAt] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [severity, setSeverity] = useState('normal')
  const [vehicleId, setVehicleId] = useState('')
  const [jobId, setJobId] = useState('')
  const [requestId, setRequestId] = useState('')
  const [agentId, setAgentId] = useState('')
  const [locationText, setLocationText] = useState('')

  useEffect(() => {
    if (!open) return
    const d = new Date(incident.occurred_at)
    setOccurredAt(new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16))
    setTitle(incident.title); setDescription(incident.description); setSeverity(incident.severity)
    setVehicleId(incident.vehicle_id ?? ''); setJobId(incident.field_job_id ?? '')
    setRequestId(incident.request_id ?? ''); setAgentId(incident.involved_agent_id ?? '')
    setLocationText(incident.location_text ?? '')
  }, [open, incident])

  const save = useMutation({
    mutationFn: async () => {
      if (!title.trim()) throw new Error('Title is required')
      if (!description.trim()) throw new Error('Description is required')
      if (!occurredAt) throw new Error('Occurred-at is required')
      // Single UPDATE → one audit_events amendment row carrying every changed field.
      const { error } = await supabase.from('incidents').update({
        occurred_at: new Date(occurredAt).toISOString(),
        title: title.trim(),
        description: description.trim(),
        severity,
        location_text: locationText.trim() || null,
        vehicle_id: vehicleId || null,
        field_job_id: jobId || null,
        request_id: requestId || null,
        involved_agent_id: agentId || null,
      }).eq('id', incident.id)
      if (error) throw error
    },
    onSuccess: () => { toast.success('Incident amended'); onSaved() },
    onError: (e: any) => toast.error(e?.message || 'Failed to amend incident'),
  })
  const canSave = !!title.trim() && !!description.trim() && !!occurredAt && !save.isPending

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
          style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'var(--overlay-scrim)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <motion.div initial={{ scale: 0.97, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.97, opacity: 0 }} transition={{ duration: 0.15 }} onClick={e => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 480, background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 16, padding: 20, display: 'flex', flexDirection: 'column', gap: 12, maxHeight: '92vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.02em' }}>Amend incident</h2>
              <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'flex', padding: 2 }}><X style={{ width: 18, height: 18 }} /></button>
            </div>
            <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '-4px 0 0' }}>Every change is recorded in the timeline. Status is changed from the incident page.</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div><label style={lbl}>Occurred at</label><input type="datetime-local" value={occurredAt} onChange={e => setOccurredAt(e.target.value)} style={{ ...field, colorScheme: 'dark' }} /></div>
              <div><label style={lbl}>Severity</label><select value={severity} onChange={e => setSeverity(e.target.value)} style={{ ...field, cursor: 'pointer' }}>
                <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
              </select></div>
            </div>
            <div><label style={lbl}>Title</label><input value={title} onChange={e => setTitle(e.target.value)} style={{ ...field, fontWeight: 600 }} /></div>
            <div><label style={lbl}>Description</label><textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} style={{ ...field, resize: 'vertical' }} /></div>
            <div><label style={lbl}>Location</label><input value={locationText} onChange={e => setLocationText(e.target.value)} placeholder="Optional — address or site" style={field} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div><label style={lbl}>Vehicle</label><select value={vehicleId} onChange={e => setVehicleId(e.target.value)} style={{ ...field, cursor: 'pointer' }}>
                <option value="">None</option>{vehicles.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select></div>
              <div><label style={lbl}>Job</label><select value={jobId} onChange={e => setJobId(e.target.value)} style={{ ...field, cursor: 'pointer' }}>
                <option value="">None</option>{jobs.map(j => <option key={j.id} value={j.id}>{j.job_number || j.title || 'Job'}</option>)}
              </select></div>
              <div><label style={lbl}>Request</label><select value={requestId} onChange={e => setRequestId(e.target.value)} style={{ ...field, cursor: 'pointer' }}>
                <option value="">None</option>{requests.map(r => <option key={r.id} value={r.id}>{r.subject}</option>)}
              </select></div>
              <div><label style={lbl}>Involved tech</label><select value={agentId} onChange={e => setAgentId(e.target.value)} style={{ ...field, cursor: 'pointer' }}>
                <option value="">None</option>{profiles.map(p => <option key={p.id} value={p.id}>{p.full_name ?? 'Unnamed'}</option>)}
              </select></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
              <button type="button" onClick={onClose} style={{ padding: '8px 14px', background: 'var(--border-subtle)', border: 'none', borderRadius: 8, cursor: 'pointer', color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600 }}>Cancel</button>
              <button type="button" onClick={() => save.mutate()} disabled={!canSave}
                style={{ padding: '8px 16px', background: 'var(--accent)', border: 'none', borderRadius: 8, cursor: canSave ? 'pointer' : 'not-allowed', color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, opacity: canSave ? 1 : 0.5 }}>
                {save.isPending ? 'Saving…' : 'Save amendment'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

// ─── shared style atoms ───────────────────────────────────────────────────────
const ic: React.CSSProperties = { width: 12, height: 12 }
const ic2: React.CSSProperties = { width: 14, height: 14 }
const sectionLbl: React.CSSProperties = { fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }
const linkBtn: React.CSSProperties = { background: 'none', border: 'none', color: 'var(--accent-text)', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 }
const secondaryBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'var(--surface-sunken)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 12, fontWeight: 600 }
const chipLink: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--accent-text)', textDecoration: 'none', fontWeight: 600 }
function pill(bg: string, color: string): React.CSSProperties { return { fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: bg, color } }
function chip(icon: React.ReactNode, text: string) {
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--ink-tertiary)' }}>{icon}{text}</span>
}
